import { MetricCard, type MetricCardTone } from "@components";
import {
	AlertTriangle,
	Announcement02,
	Hourglass01,
	Wallet02,
} from "@untitledui/icons";
import {
	INFRACTION_METRIC_CARDS,
	INFRACTION_METRIC_FILTER,
} from "../constants/infraction-metrics.constant";
import type {
	InfractionMetricFilter,
	InfractionMetricsSummary,
} from "../types/infraction.types";
import { formatCurrencyBrl } from "../utils/formatters";

type InfractionsMetricsCardsProps = {
	metrics: InfractionMetricsSummary;
	activeFilter: InfractionMetricFilter;
	isLoading?: boolean;
	onFilterChange: (filter: InfractionMetricFilter) => void;
};

function getMetricValue(
	key: InfractionMetricFilter,
	metrics: InfractionMetricsSummary
): string | number {
	switch (key) {
		case INFRACTION_METRIC_FILTER.CRITICAL:
			return metrics.criticalCount;
		case INFRACTION_METRIC_FILTER.AWAITING_INDICATION:
			return metrics.awaitingIndicationCount;
		case INFRACTION_METRIC_FILTER.NIC_RISK:
			return formatCurrencyBrl(metrics.nicRiskAmount);
		case INFRACTION_METRIC_FILTER.PENDING_TOTAL:
			return formatCurrencyBrl(metrics.pendingTotalAmount);
		default:
			return 0;
	}
}

function getMetricIcon(key: InfractionMetricFilter) {
	switch (key) {
		case INFRACTION_METRIC_FILTER.CRITICAL:
			return <AlertTriangle className="size-full" />;
		case INFRACTION_METRIC_FILTER.AWAITING_INDICATION:
			return <Hourglass01 className="size-full" />;
		case INFRACTION_METRIC_FILTER.NIC_RISK:
			return <Announcement02 className="size-full" />;
		default:
			return <Wallet02 className="size-full" />;
	}
}

function getMetricTone(key: InfractionMetricFilter): MetricCardTone {
	switch (key) {
		case INFRACTION_METRIC_FILTER.CRITICAL:
			return "error";
		case INFRACTION_METRIC_FILTER.AWAITING_INDICATION:
			return "warning";
		case INFRACTION_METRIC_FILTER.NIC_RISK:
			return "info";
		default:
			return "neutral";
	}
}

export function InfractionsMetricsCards({
	metrics,
	activeFilter,
	isLoading = false,
	onFilterChange,
}: InfractionsMetricsCardsProps) {
	return (
		<div
			className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
			data-slot="infractions-metrics-cards"
		>
			{INFRACTION_METRIC_CARDS.map((card) => {
				const isSelected = activeFilter === card.key;

				const handleClick = () => {
					onFilterChange(isSelected ? INFRACTION_METRIC_FILTER.ALL : card.key);
				};

				return (
					<MetricCard
						icon={getMetricIcon(card.key)}
						isLoading={isLoading}
						key={card.key}
						onClick={handleClick}
						selectable
						selected={isSelected}
						subtitle={card.subtitle}
						title={card.title}
						tone={getMetricTone(card.key)}
						value={getMetricValue(card.key, metrics)}
					/>
				);
			})}
		</div>
	);
}
